import { useCallback } from "react";
import { CACHE_KEYS, getProfile, getSettings, getTips, putSetting, putSettings, putTip, type SettingChange, type UserProfile, updateName } from "../client";
import type { SettingKey, TipKey, UserSettings, UserTips } from "../settings";
import { DEFAULT_SETTINGS } from "../settings";
import { useCacheValue } from "./useCacheValue";

const PROFILE_KEYS = [CACHE_KEYS.profile];
const SETTINGS_KEYS = [CACHE_KEYS.settings];
const TIPS_KEYS = [CACHE_KEYS.tips];

export type UseProfileResult = {
  /** Null while loading and when signed out. */
  profile: UserProfile | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  updateName: (name: string) => Promise<string>;
};

export const useProfile = (): UseProfileResult => {
  const { value, isLoading, error, refresh } = useCacheValue(getProfile, PROFILE_KEYS, null);
  return { profile: value, isLoading, error, refresh, updateName };
};

export type UseSettingsResult = {
  settings: UserSettings;
  isLoading: boolean;
  error: string | null;
  setSetting: (key: SettingKey, value: string) => Promise<void>;
  setSettings: (changes: SettingChange[]) => Promise<void>;
};

const loadSettings = (): Promise<UserSettings> => getSettings();

/** Settings with their defaults; they work signed out too, from the cache. */
export const useSettings = (): UseSettingsResult => {
  const { value, isLoading, error } = useCacheValue(loadSettings, SETTINGS_KEYS, DEFAULT_SETTINGS as UserSettings);
  return { settings: value, isLoading, error, setSetting: putSetting, setSettings: putSettings };
};

export type UseTipResult = {
  dismissed: boolean;
  isLoading: boolean;
  dismiss: () => Promise<void>;
  restore: () => Promise<void>;
};

const loadTips = (): Promise<UserTips> => getTips();

/** A dismissable hint. `dismissed` is true while loading so the hint does not flash. */
export const useTip = (key: TipKey): UseTipResult => {
  const { value, isLoading } = useCacheValue(loadTips, TIPS_KEYS, {} as UserTips);
  const dismiss = useCallback(() => putTip(key, true), [key]);
  const restore = useCallback(() => putTip(key, false), [key]);
  return { dismissed: isLoading || value[key] === true, isLoading, dismiss, restore };
};
